// =============================================================================
// HISTORICAL BACKFILL - waiting for the merchant to decide
// =============================================================================
// On install we import 90 days of past orders. They are captured, and GA4
// receives them straight away (within its own 72h backdating window), so this
// dashboard has data on day one.
//
// They are NOT sent to Google Ads / Meta / TikTok / Pinterest / Snapchat until
// the merchant says so. Those are their ad accounts and their attribution, and
// if they already ran another tracking tool over the same period, sending ours
// would silently double their history. Silence is not consent for someone
// else's ad account.
//
// The banner disappears for good once released: Shop.backfillAdOptInAt is set
// and no further historical order is ever held.
//
// Copy is translated in all seven UI languages (i18n/translations.ts,
// `backfillHold`). Singular and plural are two separate strings rather than a
// suffix rule, because "1 order is / 2 orders are" does not survive translation
// as a concatenation.
// =============================================================================

import { Banner, BlockStack, Text } from '@shopify/polaris';
import { useBackfillHold, useReleaseBackfillHold } from '../hooks/api';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';

export function BackfillHoldBanner() {
  const { data } = useBackfillHold();
  const release = useReleaseBackfillHold();
  const t = useTranslation();
  const { language } = useLanguage();

  // Nothing waiting, or already answered: say nothing.
  if (!data || data.releasedAt || data.held === 0) return null;

  const c = t.backfillHold;
  const count = data.held.toLocaleString(localeMap[language]);
  const title = (data.held === 1 ? c.titleOne : c.titleMany).replace('{n}', count);

  return (
    <Banner
      title={title}
      tone="info"
      action={{
        content: release.isPending ? c.actionPending : c.action,
        onAction: () => release.mutate(),
        loading: release.isPending,
      }}
    >
      <BlockStack gap="200">
        <Text as="p">{c.imported}</Text>
        <Text as="p">{c.notSent}</Text>
        {/* Said upfront so the number that lands is not a surprise: most of a
            90-day import is already outside the platforms' own acceptance
            windows, and those events could never have been counted anyway. */}
        <Text as="p" tone="subdued">{c.windows}</Text>
        {release.isError && <Text as="p" tone="critical">{c.error}</Text>}
        {release.isSuccess && <Text as="p" tone="success">{c.success}</Text>}
      </BlockStack>
    </Banner>
  );
}
